/**
 * ContentPublishWorkflowPage — Story 4.1: Education Content Service & Admin Publish Workflow
 *
 * Admin UI for managing education content items with a publish approval workflow.
 * Requires staff:admin role (enforced at route level in app.tsx).
 *
 * Features:
 *   - Managed list: title, contentType, format, clinicalTracks, languageCode, status badge
 *   - Filterable by status (draft/pending_review/published/all)
 *   - Searchable by title (client-side)
 *   - Each row opens editor
 *   - "New Content" action opens create form
 *   - Full field editor: all ContentItem fields
 *   - Submit for review workflow
 *   - Sign-off panel: English (clinical only) vs Spanish (clinical + translator)
 *   - Spanish dual sign-off confirmation dialog
 *   - Status badge colours: draft=neutral grey, pending_review=amber, published=teal
 *
 * @NonPhiEndpoint — education content is clinical corpus, NOT member PHI.
 */

import { usePortalAuth, useRoles } from '@nrx/auth-ui';
import { Table } from '@nrx/ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

import { SERVICES_BASE_URL } from '../../lib/services-base.js';

const CLINICAL_TRACK_OPTIONS = ['eCKM', 'CKM'] as const;
const CONTENT_TYPE_OPTIONS = ['lesson', 'quiz', 'recipe-card', 'shopping-guide'] as const;
const FORMAT_OPTIONS = ['watch', 'listen', 'read'] as const;
const LANGUAGE_OPTIONS = ['en', 'es'] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

type ContentStatus = 'draft' | 'pending_review' | 'published';
type LanguageCode = 'en' | 'es';
type ClinicalTrack = 'eCKM' | 'CKM';
type ContentType = 'lesson' | 'quiz' | 'recipe-card' | 'shopping-guide';
type ContentFormat = 'watch' | 'listen' | 'read';
type SignOffRole = 'clinical' | 'translator';

interface SignOff {
  signedOffBy: string;
  role: SignOffRole;
  signedOffAt: string;
}

interface ContentItemDto {
  id: string;
  title: string;
  description?: string | null;
  contentType: ContentType;
  format: ContentFormat;
  clinicalTracks: ClinicalTrack[];
  phases: string[];
  languageCode: LanguageCode;
  parentContentId?: string | null;
  status: ContentStatus;
  versionNumber: number;
  authorId: string;
  authoredAt: string;
  signOffs: SignOff[];
  publishedAt?: string | null;
  publishedBy?: string | null;
  updatedAt: string;
}

interface ContentFormState {
  title: string;
  description: string;
  contentType: ContentType;
  format: ContentFormat;
  clinicalTracks: ClinicalTrack[];
  phases: string[];
  languageCode: LanguageCode;
  parentContentId: string;
}

// ─── Status badge helper ──────────────────────────────────────────────────────

function statusBadgeClass(status: ContentStatus): string {
  switch (status) {
    case 'draft':
      return 'inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700';
    case 'pending_review':
      return 'inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800';
    case 'published':
      return 'inline-flex items-center rounded-full bg-teal-100 px-2.5 py-0.5 text-xs font-medium text-teal-800';
    default:
      return 'inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700';
  }
}

// ─── Empty form factory ───────────────────────────────────────────────────────

function emptyForm(): ContentFormState {
  return {
    title: '',
    description: '',
    contentType: 'lesson',
    format: 'read',
    clinicalTracks: [],
    phases: [],
    languageCode: 'en',
    parentContentId: '',
  };
}

function itemToForm(item: ContentItemDto): ContentFormState {
  return {
    title: item.title,
    description: item.description ?? '',
    contentType: item.contentType,
    format: item.format,
    clinicalTracks: item.clinicalTracks,
    phases: item.phases,
    languageCode: item.languageCode,
    parentContentId: item.parentContentId ?? '',
  };
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ContentPublishWorkflowPage(): JSX.Element {
  const auth = usePortalAuth();
  const roles = useRoles();
  const isAdmin = roles.includes('staff:admin');

  const [items, setItems] = useState<ContentItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Editor state
  const [selectedItem, setSelectedItem] = useState<ContentItemDto | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [form, setForm] = useState<ContentFormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Spanish dual sign-off dialog
  const [showSpanishConfirmDialog, setShowSpanishConfirmDialog] = useState(false);
  const [pendingSignOffRole, setPendingSignOffRole] = useState<SignOffRole | null>(null);

  const authHeader = `Bearer ${String(auth.user?.access_token ?? '')}`;

  // ─── Load content list ──────────────────────────────────────────────────────

  const loadContent = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') {
        params.set('status', statusFilter);
      }
      const url = `${SERVICES_BASE_URL}/v1/engagement/admin/v1/content${params.size > 0 ? `?${params.toString()}` : ''}`;
      const res = await fetch(url, {
        headers: { Authorization: authHeader },
      });
      if (!res.ok) {
        setError(`Failed to load content (${String(res.status)})`);
        return;
      }
      const data = (await res.json()) as { items: ContentItemDto[] };
      setItems(data.items);
    } catch {
      setError('Network error loading content');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, authHeader]);

  useEffect(() => {
    void loadContent();
  }, [loadContent]);

  // ─── Filtered display ───────────────────────────────────────────────────────

  const filteredItems = items.filter((item) =>
    searchQuery === '' ? true : item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // ─── Create new content ─────────────────────────────────────────────────────

  const handleNewContent = (): void => {
    setSelectedItem(null);
    setForm(emptyForm());
    setIsCreating(true);
    setShowEditor(true);
    setSaveError(null);
  };

  // ─── Edit existing content ──────────────────────────────────────────────────

  const handleEditItem = (item: ContentItemDto): void => {
    setSelectedItem(item);
    setForm(itemToForm(item));
    setIsCreating(false);
    setShowEditor(true);
    setSaveError(null);
  };

  // ─── Save draft ─────────────────────────────────────────────────────────────

  const handleSaveDraft = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      const url = isCreating
        ? `${SERVICES_BASE_URL}/v1/engagement/admin/v1/content`
        : `${SERVICES_BASE_URL}/v1/engagement/admin/v1/content/${selectedItem?.id ?? ''}`;
      const method = isCreating ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method,
        headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: form.title,
          description: form.description || undefined,
          contentType: form.contentType,
          format: form.format,
          clinicalTracks: form.clinicalTracks,
          phases: form.phases,
          languageCode: form.languageCode,
          parentContentId: form.parentContentId || undefined,
        }),
      });
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setSaveError(errBody.detail ?? 'Save failed');
        return;
      }
      const saved = (await res.json()) as ContentItemDto;
      if (isCreating) {
        setItems((prev) => [saved, ...prev]);
      } else {
        setItems((prev) => prev.map((i) => (i.id === saved.id ? saved : i)));
      }
      setSelectedItem(saved);
      setIsCreating(false);
    } catch {
      setSaveError('Network error saving content');
    } finally {
      setSaving(false);
    }
  };

  // ─── Submit for review ──────────────────────────────────────────────────────

  const handleSubmitForReview = async (): Promise<void> => {
    if (!selectedItem) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/engagement/admin/v1/content/${selectedItem.id}/submit-for-review`,
        {
          method: 'POST',
          headers: { Authorization: authHeader },
        },
      );
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setSaveError(errBody.detail ?? 'Submit for review failed');
        return;
      }
      const updated = (await res.json()) as ContentItemDto;
      setSelectedItem(updated);
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch {
      setSaveError('Network error submitting for review');
    } finally {
      setSaving(false);
    }
  };

  // ─── Sign-off ───────────────────────────────────────────────────────────────

  const handleSignOff = (role: SignOffRole): void => {
    // Spanish dual sign-off: show confirmation dialog before sending
    if (selectedItem?.languageCode === 'es') {
      setPendingSignOffRole(role);
      setShowSpanishConfirmDialog(true);
      return;
    }
    void doSignOff(role);
  };

  const doSignOff = async (role: SignOffRole): Promise<void> => {
    if (!selectedItem) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(
        `${SERVICES_BASE_URL}/v1/engagement/admin/v1/content/${selectedItem.id}/sign-off`,
        {
          method: 'POST',
          headers: { Authorization: authHeader, 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        },
      );
      if (!res.ok) {
        const errBody = (await res.json()) as { detail?: string };
        setSaveError(errBody.detail ?? 'Sign-off failed');
        return;
      }
      const updated = (await res.json()) as ContentItemDto;
      setSelectedItem(updated);
      setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    } catch {
      setSaveError('Network error recording sign-off');
    } finally {
      setSaving(false);
    }
  };

  const confirmSpanishSignOff = (): void => {
    setShowSpanishConfirmDialog(false);
    if (pendingSignOffRole) {
      void doSignOff(pendingSignOffRole);
    }
    setPendingSignOffRole(null);
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Content Publish Workflow</h1>
        {isAdmin && (
          <button
            type="button"
            onClick={handleNewContent}
            className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          >
            New Content
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ContentStatus | 'all');
          }}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm"
          aria-label="Filter by status"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending_review">Pending review</option>
          <option value="published">Published</option>
        </select>
        <input
          type="text"
          placeholder="Search by title…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
          aria-label="Search content by title"
        />
      </div>

      {/* Error banner */}
      {error && (
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {/* Content list */}
      {!showEditor && (
        <div className="overflow-hidden rounded-lg border border-gray-200">
          <Table.ScrollArea loading={loading} loadingLabel="Loading content…">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Format</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Tracks</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Language</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredItems.map((item) => (
                  <Table.Row key={item.id} className="hover:bg-gray-50">
                    <Table.Cell className="font-medium text-gray-900">{item.title}</Table.Cell>
                    <Table.Cell className="text-gray-600">{item.contentType}</Table.Cell>
                    <Table.Cell className="text-gray-600">{item.format}</Table.Cell>
                    <Table.Cell className="text-gray-600">
                      {item.clinicalTracks.join(', ')}
                    </Table.Cell>
                    <Table.Cell className="text-gray-600">{item.languageCode}</Table.Cell>
                    <Table.Cell>
                      <span className={statusBadgeClass(item.status)}>{item.status}</span>
                    </Table.Cell>
                    <Table.Cell>
                      <button
                        type="button"
                        onClick={() => {
                          handleEditItem(item);
                        }}
                        className="text-sm text-teal-600 underline hover:text-teal-800"
                      >
                        {isAdmin ? 'Edit' : 'View'}
                      </button>
                    </Table.Cell>
                  </Table.Row>
                ))}
                {!loading && filteredItems.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={7} className="py-8 text-center text-gray-400">
                      No content items found.
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </Table.ScrollArea>
        </div>
      )}

      {/* Editor panel */}
      {showEditor && (
        <div className="rounded-lg border border-gray-200 bg-white p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {isCreating ? 'New Content Item' : `Edit: ${selectedItem?.title ?? ''}`}
            </h2>
            <button
              type="button"
              onClick={() => {
                setShowEditor(false);
              }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              ← Back to list
            </button>
          </div>

          {saveError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700" role="alert">
              {saveError}
            </div>
          )}

          {/* Form fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label
                htmlFor="content-title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Title *
              </label>
              <input
                id="content-title"
                type="text"
                value={form.title}
                onChange={(e) => {
                  setForm((f) => ({ ...f, title: e.target.value }));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                placeholder="Content title"
                disabled={!isAdmin}
              />
            </div>

            <div className="col-span-2">
              <label
                htmlFor="content-description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <textarea
                id="content-description"
                value={form.description}
                onChange={(e) => {
                  setForm((f) => ({ ...f, description: e.target.value }));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                rows={3}
                disabled={!isAdmin}
              />
            </div>

            <div>
              <label
                htmlFor="content-type"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Content Type *
              </label>
              <select
                id="content-type"
                value={form.contentType}
                onChange={(e) => {
                  setForm((f) => ({ ...f, contentType: e.target.value as ContentType }));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                disabled={!isAdmin}
              >
                {CONTENT_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="content-format"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Format *
              </label>
              <select
                id="content-format"
                value={form.format}
                onChange={(e) => {
                  setForm((f) => ({ ...f, format: e.target.value as ContentFormat }));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                disabled={!isAdmin}
              >
                {FORMAT_OPTIONS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-1">
                  Clinical Tracks * (select at least one)
                </legend>
                <div className="flex gap-3">
                  {CLINICAL_TRACK_OPTIONS.map((track) => (
                    <label key={track} className="flex items-center gap-1 text-sm">
                      <input
                        type="checkbox"
                        checked={form.clinicalTracks.includes(track)}
                        onChange={(e) => {
                          setForm((f) => ({
                            ...f,
                            clinicalTracks: e.target.checked
                              ? [...f.clinicalTracks, track]
                              : f.clinicalTracks.filter((t) => t !== track),
                          }));
                        }}
                        disabled={!isAdmin}
                      />
                      {track}
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>

            <div>
              <label
                htmlFor="content-language"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Language *
              </label>
              <select
                id="content-language"
                value={form.languageCode}
                onChange={(e) => {
                  setForm((f) => ({ ...f, languageCode: e.target.value as LanguageCode }));
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                disabled={!isAdmin}
              >
                {LANGUAGE_OPTIONS.map((l) => (
                  <option key={l} value={l}>
                    {l === 'en' ? 'English' : 'Spanish'}
                  </option>
                ))}
              </select>
            </div>

            {form.languageCode === 'es' && (
              <div className="col-span-2">
                <label
                  htmlFor="parent-content-id"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Parent English Content ID
                </label>
                <input
                  id="parent-content-id"
                  type="text"
                  value={form.parentContentId}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, parentContentId: e.target.value }));
                  }}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  placeholder="ULID of English parent content item"
                  disabled={!isAdmin}
                />
              </div>
            )}
          </div>

          {/* Action buttons */}
          {isAdmin && (
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  void handleSaveDraft();
                }}
                disabled={saving}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save draft'}
              </button>

              {selectedItem?.status === 'draft' && (
                <button
                  type="button"
                  onClick={() => {
                    void handleSubmitForReview();
                  }}
                  disabled={saving}
                  className="rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                >
                  Submit for review
                </button>
              )}
            </div>
          )}

          {/* Sign-off panel */}
          {selectedItem?.status === 'pending_review' && (
            <SignOffPanel
              item={selectedItem}
              onSignOff={handleSignOff}
              isAdmin={isAdmin}
              saving={saving}
            />
          )}

          {/* Published status */}
          {selectedItem?.status === 'published' && (
            <div className="rounded-md bg-teal-50 p-4 text-sm text-teal-800">
              <p className="font-medium">Published</p>
              <p>
                Published at {new Date(selectedItem.publishedAt ?? '').toLocaleString()} by{' '}
                {selectedItem.publishedBy ?? 'unknown'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Spanish dual sign-off confirmation dialog */}
      {showSpanishConfirmDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="spanish-signoff-title"
        >
          <div className="rounded-lg bg-white p-6 shadow-xl max-w-md w-full mx-4">
            <h3 id="spanish-signoff-title" className="text-lg font-semibold text-gray-900 mb-3">
              Confirm Spanish Content Sign-off
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              You are recording a <strong>{pendingSignOffRole}</strong> sign-off for Spanish
              content. Spanish content requires both clinical AND translator sign-offs before it can
              be published. Please confirm that you have reviewed the translation for clinical
              accuracy.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowSpanishConfirmDialog(false);
                  setPendingSignOffRole(null);
                }}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSpanishSignOff}
                className="rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
              >
                Confirm sign-off
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sign-off Panel Sub-component ────────────────────────────────────────────

interface SignOffPanelProps {
  item: ContentItemDto;
  onSignOff: (role: SignOffRole) => void;
  isAdmin: boolean;
  saving: boolean;
}

function SignOffPanel({ item, onSignOff, isAdmin, saving }: SignOffPanelProps): JSX.Element {
  const hasClinical = item.signOffs.some((s) => s.role === 'clinical');
  const hasTranslator = item.signOffs.some((s) => s.role === 'translator');
  const isSpanish = item.languageCode === 'es';

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4 space-y-3">
      <h3 className="text-sm font-semibold text-amber-900">Sign-off Required</h3>

      {isSpanish && (
        <p className="text-xs text-amber-800">
          Spanish content requires both <strong>clinical</strong> and <strong>translator</strong>{' '}
          sign-offs before publishing.
        </p>
      )}

      <div className="space-y-2">
        {/* Clinical sign-off */}
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm font-medium text-gray-800">Clinical sign-off</span>
            {hasClinical && (
              <span className="ml-2 text-xs text-teal-700">
                ✓ Signed by {item.signOffs.find((s) => s.role === 'clinical')?.signedOffBy ?? ''}
              </span>
            )}
          </div>
          {!hasClinical && isAdmin && (
            <button
              type="button"
              onClick={() => {
                onSignOff('clinical');
              }}
              disabled={saving}
              className="rounded-md bg-teal-600 px-3 py-1 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              Record clinical sign-off
            </button>
          )}
        </div>

        {/* Translator sign-off (Spanish only) */}
        {isSpanish && (
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-800">Translator sign-off</span>
              {hasTranslator && (
                <span className="ml-2 text-xs text-teal-700">
                  ✓ Signed by{' '}
                  {item.signOffs.find((s) => s.role === 'translator')?.signedOffBy ?? ''}
                </span>
              )}
            </div>
            {!hasTranslator && isAdmin && (
              <button
                type="button"
                onClick={() => {
                  onSignOff('translator');
                }}
                disabled={saving}
                className="rounded-md bg-teal-600 px-3 py-1 text-xs font-medium text-white hover:bg-teal-700 disabled:opacity-50"
              >
                Record translator sign-off
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
